/**
 * Sample answers of `/sales-truth/*` for the page "Learn from sales" (20 Oct 2026): the
 * render tests fill the query cache with them, and the Vite dev server shows them when the
 * backend does not answer (`learn-from-sales-page.tsx`, marked as sample figures).
 *
 * Invented data only: invented firms, accounts, item numbers (`0990001`), quotations
 * (`SQ0000001`), sales orders (`SO0000001`), case ids and names. `wgg` (Germany) rows carry
 * no person, as the server sends them.
 */

import type {
  CaseGroundTruth,
  SalesTruthInquiry,
  SalesTruthInquiryDetail,
  SalesTruthInquiryList,
  SalesTruthPeople,
  SalesTruthSummary,
  SalesTruthTrail,
  TrailScores,
  TruthLearning,
  TruthLearningList,
} from "@/lib/api";

const NOW = "2026-10-20T08:00:00+00:00";

export function sampleSalesTruthSummary(): SalesTruthSummary {
  return {
    window_days: 30,
    as_of: NOW,
    kpis: {
      inquiries: 120,
      linked: 60,
      linked_share: 0.5,
      with_order: 24,
      order_share: 0.4,
      agreement_mean: 0.55,
      matched_full: 27,
      matched_share: 0.5,
      ordered_lines: 80,
      ordered_found: 40,
      ordered_found_share: 0.5,
      orders_with_lines: 24,
      orders_all_found: 11,
      orders_with_po: 23,
      orders_with_po_key: 21,
      po_in_mail: 14,
    },
    funnel: [
      {
        area: "dk",
        inquiries: 50,
        linked: 30,
        with_order: 12,
        ordered_lines: 40,
        ordered_found: 22,
      },
      {
        area: "usl",
        inquiries: 40,
        linked: 20,
        with_order: 9,
        ordered_lines: 30,
        ordered_found: 13,
      },
      {
        area: "wgg",
        inquiries: 30,
        linked: 10,
        with_order: 3,
        ordered_lines: 10,
        ordered_found: 5,
      },
    ],
    sources: [
      { source: "fo_quotation", certainty: "exact", count: 25 },
      { source: "fo_thread", certainty: "exact", count: 20 },
      { source: "fo_contact", certainty: "inferred", count: 10 },
      { source: "mail_offer", certainty: "strong", count: 5 },
    ],
    differences: {
      same_item: 90,
      other_item: 14,
      missed: 11,
      extra: 6,
      qty_differs: 4,
      price_differs: 3,
      not_quoted: 2,
    },
    open_in_i2q: 7,
    learnings: {
      observed: 18,
      confirmed: 40,
      proposed: 0,
      active: 0,
      conflict: 2,
      unaligned: 5,
      retracted: 0,
    },
    switches: { observe: true, apply: false, auto_activate: false, multi_account: false },
  };
}

function inquiry(overrides: Partial<SalesTruthInquiry> = {}): SalesTruthInquiry {
  return {
    case_id: "RFQ-LFS-0001",
    received_at: "2026-10-14T09:12:00+00:00",
    area: "dk",
    subject: "Inquiry for wear items, Example Foundry A/S",
    i2q_customer: "C-0001",
    i2q_customer_name: "Example Foundry A/S",
    fo_customer: "C-0001",
    customer_match: "same",
    quotation: "SQ0000001",
    sales_order: "SO0000001",
    quote_status: "won",
    quote_created_at: "2026-10-14T13:40:00+00:00",
    sales_person: "Alex Example",
    sales_person_id: "T-0001",
    person_hidden: false,
    source: "fo_thread",
    certainty: "exact",
    line_agreement: 0.5,
    line_count: 4,
    modified_after_reply: false,
    learnings: 2,
    conflicts: 0,
    open_in_i2q: false,
    customer_po: "PO-2026-0101",
    po_mail_case_id: "RFQ-LFS-0091",
    ...overrides,
  };
}

export function sampleSalesTruthInquiries(): SalesTruthInquiryList {
  return {
    total: 3,
    limit: 50,
    offset: 0,
    rows: [
      inquiry(),
      inquiry({
        case_id: "RFQ-LFS-0002",
        area: "usl",
        subject: "RFQ shot blast items",
        i2q_customer: null,
        fo_customer: "C-0002",
        customer_match: "i2q_none",
        quotation: "SQ0000002",
        sales_order: null,
        customer_po: null,
        po_mail_case_id: null,
        quote_status: "open",
        sales_person: "Sam Example",
        sales_person_id: "T-0002",
        source: "fo_contact",
        certainty: "inferred",
        line_agreement: 0,
        line_count: 2,
        learnings: 1,
        conflicts: 1,
        open_in_i2q: true,
      }),
      inquiry({
        case_id: "RFQ-LFS-0003",
        area: "wgg",
        subject: "Anfrage Ersatzteile",
        quotation: "SQ0000003",
        sales_order: null,
        customer_po: null,
        po_mail_case_id: null,
        quote_status: "lost",
        sales_person: null,
        sales_person_id: null,
        person_hidden: true,
        source: "fo_quotation",
        line_agreement: 1,
        line_count: 3,
        learnings: 0,
      }),
    ],
  };
}

export function sampleSalesTruthPeople(): SalesTruthPeople {
  return {
    rows: [
      {
        sales_person_id: "T-0001",
        sales_person: "Alex Example",
        areas: ["dk"],
        linked: 30,
        with_order: 12,
        i2q_matched: 14,
        with_agreement: 28,
        learnings: 9,
      },
      {
        sales_person_id: "T-0002",
        sales_person: "Sam Example",
        areas: ["usl"],
        linked: 20,
        with_order: 9,
        i2q_matched: 8,
        with_agreement: 19,
        learnings: 6,
      },
    ],
    hidden_areas: ["wgg"],
    hidden_linked: 10,
  };
}

function learning(overrides: Partial<TruthLearning> = {}): TruthLearning {
  return {
    learning_id: 1,
    case_id: "RFQ-LFS-0001",
    area: "dk",
    kind: "item",
    status: "observed",
    observe_only: false,
    observe_reason: null,
    line_index: 1,
    inquiry_text: "Wear plate 4711-B",
    i2q_value: "0990002",
    sales_value: "0990003",
    customer_account: "C-0001",
    alignment: "qty",
    source: "fo_thread",
    quotation: "SQ0000001",
    sales_order: "SO0000001",
    line_ordered: true,
    quote_created_at: "2026-10-14T13:40:00+00:00",
    sales_person: "Alex Example",
    person_hidden: false,
    alias_id: null,
    hits: 0,
    note: null,
    observed_at: NOW,
    updated_at: NOW,
    decided_by: null,
    ...overrides,
  };
}

export function sampleTruthLearnings(): TruthLearningList {
  return {
    total: 4,
    limit: 50,
    offset: 0,
    rows: [
      learning(),
      learning({
        learning_id: 2,
        case_id: "RFQ-LFS-0002",
        area: "usl",
        status: "conflict",
        inquiry_text: "Impeller 12-A",
        i2q_value: null,
        sales_value: "0990004",
        source: "fo_contact",
        observe_only: true,
        observe_reason: "fo_contact",
        quotation: "SQ0000002",
        sales_order: null,
        sales_person: "Sam Example",
        note: "Quotation SQ0000009 gives 0990005.",
      }),
      learning({
        learning_id: 3,
        kind: "customer",
        status: "active",
        line_index: null,
        inquiry_text: "buyer@example-foundry.example",
        i2q_value: null,
        sales_value: "C-0001",
        alias_id: "A-0001",
        hits: 4,
        sender_multi_account: false,
      }),
      // decision D5: a distributor whose quotations give two accounts
      learning({
        learning_id: 4,
        case_id: "RFQ-LFS-0003",
        kind: "customer",
        status: "conflict",
        line_index: null,
        inquiry_text: "orders@example-distributor.example",
        i2q_value: "C-0002",
        sales_value: "C-0003",
        quotation: "SQ0000004",
        note: "Quotation SQ0000005 gives C-0002.",
        sender_multi_account: true,
      }),
    ],
    counts: {
      observed: 1,
      confirmed: 0,
      proposed: 0,
      active: 1,
      conflict: 2,
      unaligned: 0,
      retracted: 0,
      multi_account_senders: 1,
    },
    apply_on: true,
    multi_account_on: true,
  };
}

export function sampleCaseGroundTruth(overrides: Partial<CaseGroundTruth> = {}): CaseGroundTruth {
  return {
    source: "fo_thread",
    certainty: "exact",
    entity: "dk",
    quotation: "SQ0000001",
    sales_order: "SO0000001",
    quote_status: "won",
    quote_created_at: "2026-10-14T13:40:00+00:00",
    sales_person: "Alex Example",
    person_hidden: false,
    customer_number: "C-0001",
    line_agreement: 0.5,
    line_count: 4,
    modified_after_reply: false,
    answer_case_id: null,
    checked_at: NOW,
    diff: [
      {
        verdict: "same_item",
        draft_index: 0,
        inquiry_text: "Blast wheel 0990001",
        i2q_item: "0990001",
        i2q_qty: 2,
        i2q_price: 41.5,
        sales_item: "0990001",
        sales_description: "BLAST WHEEL",
        sales_qty: 2,
        sales_price: 41.5,
        sales_line_no: 1,
        ordered: true,
      },
      {
        verdict: "other_item",
        draft_index: 1,
        inquiry_text: "Wear plate 4711-B",
        i2q_item: "0990002",
        i2q_qty: 4,
        sales_item: "0990003",
        sales_description: "WEAR PLATE, HARDENED",
        sales_qty: 4,
        sales_line_no: 2,
        ordered: true,
      },
      {
        verdict: "qty_differs",
        draft_index: 2,
        inquiry_text: "Gasket set",
        i2q_item: "0990006",
        i2q_qty: 1,
        sales_item: "0990006",
        sales_description: "GASKET SET",
        sales_qty: 3,
        sales_line_no: 3,
        ordered: true,
      },
      {
        verdict: "missed",
        draft_index: null,
        sales_item: "0990007",
        sales_description: "FREIGHT INSURANCE PLATE",
        sales_qty: 1,
        sales_line_no: 4,
        ordered: false,
      },
    ],
    diff_counts: {
      same_item: 1,
      other_item: 1,
      missed: 1,
      extra: 0,
      qty_differs: 1,
      price_differs: 0,
      not_quoted: 0,
    },
    order_lines_total: 4,
    order_lines_found: 2,
    ordered_quoted: 3,
    order_only: [{ item: "0990009", qty: 2, line_no: 5 }],
    customer_po: "PO-2026-0101",
    po_mail_case_id: "RFQ-LFS-0091",
    po_mail_at: "2026-10-16T07:30:00+00:00",
    ...overrides,
  };
}

export function sampleSalesTruthInquiryDetail(): SalesTruthInquiryDetail {
  const list = sampleTruthLearnings();
  return {
    inquiry: inquiry(),
    link: sampleCaseGroundTruth(),
    learnings: (list.rows ?? []).filter((row) => row.case_id === "RFQ-LFS-0001"),
    apply_on: true,
    multi_account_on: true,
  };
}

/** The scores of one area of the tab "Email to order". */
function sampleTrailScores(area: string, overrides: Partial<TrailScores> = {}): TrailScores {
  return {
    area,
    inquiries: 40,
    linked: 22,
    with_order: 9,
    strings: 2,
    mails: 4,
    fully_right: 1,
    customer_right: 1,
    customer_sister: 1,
    ordered_lines: 3,
    ordered_found: 2,
    quoted_lines: 3,
    qty_same: 1,
    qty_compared: 2,
    read_strings: 2,
    matched_lines: 2,
    matched_trusted: 1,
    single_strings: 1,
    single_right: 1,
    multi_strings: 1,
    multi_right: 0,
    tags: {
      no_lines: 0,
      fewer_lines: 0,
      missed_lines: 0,
      wrong_part: 1,
      sure_but_wrong: 1,
      qty_differs: 1,
      wrong_customer: 1,
      no_customer: 0,
    },
    overall_share: 0.5,
    customer_share: 0.5,
    customer_sister_share: 1,
    items_share: 2 / 3,
    qty_share: 0.5,
    read_share: 1,
    parts_quote_share: 2 / 3,
    trust_share: 0.5,
    single_share: 1,
    multi_share: 0,
    touch_1_strings: 1,
    touch_1_right: 1,
    touch_2_3_strings: 0,
    touch_2_3_right: 0,
    touch_4_strings: 1,
    touch_4_right: 0,
    touch_1_share: 1,
    touch_2_3_share: null,
    touch_4_share: 0,
    reasons: {
      item_in_mail_text: 1,
      customer_sister: 1,
      qty_read_differs: 1,
    },
    ...overrides,
  };
}

/** The tab "Email to order": one dk area with a right string and a string of three mails. */
export function sampleSalesTruthTrail(): SalesTruthTrail {
  return {
    window_days: 30,
    as_of: NOW,
    limit: 300,
    overall: sampleTrailScores("all"),
    areas: [sampleTrailScores("dk")],
    strings: [
      {
        area: "dk",
        entity: "dk",
        quotation: "SQ0000001",
        quote_status: "won",
        quote_created_at: "2026-10-12T09:00:00+00:00",
        sales_order: "SO0000001",
        customer_po: "PO-4711",
        inquiries: 1,
        colleague_notes: 0,
        mails: [
          {
            case_id: "RFQ-000000000001",
            relation: "inquiry",
            received_at: "2026-10-11T08:00:00+00:00",
            sender_name: "Alex Example",
            sender_hidden: false,
            subject: "Plates for the line",
            thread_lines: 0,
          },
        ],
        i2q_customer: { account: "203797", name: "Example Foundry A/S" },
        quoted_customer: { account: "203797", name: "Example Foundry A/S" },
        customer_match: "same",
        customer_check: null,
        extraction: [
          {
            key: "0990001",
            text: "2 x plate 0990001",
            item: "0990001",
            description: "Wear plate example",
            qty: 2,
            status: "matched",
            mail: 1,
          },
        ],
        quoted: [
          {
            key: "0990001#1",
            sales_item: "0990001",
            description: "plate",
            qty: 2,
            line_no: 1,
            verdict: "same_item",
            i2q_item: "0990001",
            ordered: true,
            ordered_qty: 2,
          },
        ],
        extras: [],
        order_only: [],
        order_lines_total: 1,
        order_lines_found: 1,
        customer_right: true,
        qty_right: true,
        fully_right: true,
        tags: [],
        touches: [
          {
            at: "2026-10-11T08:00:00+00:00",
            direction: "in",
            source: "mail",
            case_id: "RFQ-000000000001",
            relation: "inquiry",
            sender_name: "Alex Example",
            sender_hidden: false,
          },
        ],
        touches_before_in: 1,
        touches_before_out: 0,
        touches_after: 0,
        quote_sent_at: "2026-10-12T09:00:00+00:00",
        quote_sent_estimated: false,
        days_to_quote: 1,
        quote_customer: { account: "203797", name: "Example Foundry A/S", invoice_account: null },
        order_customer: null,
        customer_reasons: [],
        reasons: [],
      },
      {
        area: "dk",
        entity: "dk",
        quotation: "SQ0000002",
        quote_status: "won",
        quote_created_at: "2026-10-15T09:00:00+00:00",
        sales_order: "SO0000002",
        customer_po: null,
        inquiries: 2,
        colleague_notes: 1,
        mails: [
          {
            case_id: "RFQ-000000000002",
            relation: "inquiry",
            received_at: "2026-10-13T08:00:00+00:00",
            sender_name: "Sam Example",
            sender_hidden: false,
            subject: "Liners and wheels",
            thread_lines: 0,
          },
          {
            case_id: "RFQ-000000000003",
            relation: "reply",
            received_at: "2026-10-13T10:00:00+00:00",
            sender_name: "Sam Example",
            sender_hidden: false,
            subject: "RE: Liners and wheels",
            thread_lines: 0,
          },
          {
            case_id: "RFQ-000000000004",
            relation: "inquiry",
            received_at: "2026-10-14T08:00:00+00:00",
            sender_name: "Sam Example",
            sender_hidden: false,
            subject: "RE: Liners and wheels",
            thread_lines: 1,
          },
        ],
        i2q_customer: {
          account: "203798",
          name: "Example Castings Ltd",
          invoice_account: "203700",
        },
        quoted_customer: {
          account: "203799",
          name: "Example Castings Group",
          invoice_account: "203700",
        },
        customer_match: "different",
        customer_check: {
          verdict: "sister",
          i2q: { account: "203798", name: "Example Castings Ltd", invoice_account: "203700" },
          quoted: { account: "203799", name: "Example Castings Group", invoice_account: "203700" },
        },
        extraction: [
          {
            key: "0990002",
            text: "4 liners 0990002",
            item: "0990002",
            qty: 3,
            status: "suggested",
            mail: 1,
          },
          {
            key: "0990009",
            text: "wheel 0990009",
            item: "0990009",
            qty: 1,
            status: "matched",
            mail: 3,
          },
        ],
        quoted: [
          {
            key: "0990002#1",
            sales_item: "0990002",
            description: "liner",
            qty: 4,
            line_no: 1,
            verdict: "qty_differs",
            i2q_item: "0990002",
            ordered: true,
            ordered_qty: 4,
            unit: "pcs",
            item_reasons: [],
            qty_reasons: ["read_differs"],
          },
          {
            key: "0990003#1",
            sales_item: "0990003",
            description: "wheel",
            qty: 1,
            line_no: 2,
            verdict: "other_item",
            i2q_item: "0990009",
            ordered: true,
            ordered_qty: 1,
            unit: "pcs",
            item_reasons: ["in_mail_text", "i2q_unsure"],
            qty_reasons: [],
          },
        ],
        extras: [],
        order_only: [{ item: "0990004", qty: 2, line_no: 3 }],
        order_lines_total: 2,
        order_lines_found: 1,
        customer_right: false,
        qty_right: false,
        fully_right: false,
        tags: ["wrong_part", "sure_but_wrong", "qty_differs", "wrong_customer"],
        touches: [
          {
            at: "2026-10-13T08:00:00+00:00",
            direction: "in",
            source: "mail",
            case_id: "RFQ-000000000002",
            relation: "inquiry",
            sender_name: "Sam Example",
            sender_hidden: false,
          },
          {
            at: "2026-10-13T09:30:00+00:00",
            direction: "out",
            source: "quoted",
            case_id: null,
            relation: null,
            sender_name: "Kim Seller",
            sender_hidden: false,
          },
          {
            at: "2026-10-13T10:00:00+00:00",
            direction: "in",
            source: "mail",
            case_id: "RFQ-000000000003",
            relation: "reply",
            sender_name: "Sam Example",
            sender_hidden: false,
          },
          {
            at: "2026-10-14T08:00:00+00:00",
            direction: "in",
            source: "mail",
            case_id: "RFQ-000000000004",
            relation: "inquiry",
            sender_name: "Sam Example",
            sender_hidden: false,
          },
          {
            at: "2026-10-16T08:00:00+00:00",
            direction: "out",
            source: "reply",
            case_id: "RFQ-000000000004",
            relation: null,
            sender_name: null,
            sender_hidden: false,
          },
        ],
        touches_before_in: 3,
        touches_before_out: 1,
        touches_after: 1,
        quote_sent_at: "2026-10-15T09:00:00+00:00",
        quote_sent_estimated: false,
        days_to_quote: 2,
        quote_customer: {
          account: "203799",
          name: "Example Castings Group",
          invoice_account: "203700",
        },
        order_customer: null,
        customer_how: "domain",
        customer_reasons: ["sister"],
        reasons: ["item_in_mail_text", "customer_sister", "qty_read_differs"],
      },
    ],
  };
}
